import { Paper, Grid, TextField, Stack, Button, Chip, Typography, Alert, Autocomplete } from '@mui/material';
import type { ScanEntry, ScanResolution, BinLocation } from '../../types/warehouse';

interface ScanReviewCardProps {
  draft: ScanEntry;
  onChange: (patch: Partial<ScanEntry>) => void;
  onAdd: () => void;
  onCancel: () => void;
  onNeedsMapping: () => void;
  duplicate?: boolean;
  /** Bin locations for the matched line's warehouse (empty = not bin-managed). */
  bins?: BinLocation[];
  /** Remaining receivable qty on the matched PO line (undefined = no limit). */
  maxQty?: number;
}

const RESOLUTION_META: Record<ScanResolution, { label: string; color: 'success' | 'info' | 'warning' | 'default' }> = {
  matched: { label: 'Matched', color: 'success' },
  mapped: { label: 'Mapped', color: 'info' },
  unresolved: { label: 'Needs mapping', color: 'warning' },
  created_pending: { label: 'New-item (pending)', color: 'warning' },
};

/** Editable review of a single scan before it is added to the session list. */
export default function ScanReviewCard({
  draft,
  onChange,
  onAdd,
  onCancel,
  onNeedsMapping,
  duplicate,
  bins = [],
  maxQty,
}: ScanReviewCardProps): JSX.Element {
  const meta = RESOLUTION_META[draft.resolution];
  const isUnresolved = draft.resolution === 'unresolved' || draft.resolution === 'created_pending';
  const selectedBin = bins.find((b) => b.absEntry === draft.binAbsEntry) ?? null;
  const qtyValue = draft.qty ?? 1;
  const overMax = maxQty != null && qtyValue > maxQty;
  const field = (key: keyof ScanEntry, label: string, opts: { mono?: boolean } = {}) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={(draft[key] as string) ?? ''}
      onChange={(e) => onChange({ [key]: e.target.value } as Partial<ScanEntry>)}
      InputProps={opts.mono ? { sx: { fontFamily: 'monospace' } } : undefined}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1">Review scan</Typography>
        <Chip size="small" color={meta.color} label={meta.label} />
        {draft.notOnDocument && <Chip size="small" color="warning" variant="outlined" label="Not on PO/SO" />}
        {draft.overQty && <Chip size="small" color="error" variant="outlined" label="Over qty" />}
      </Stack>

      {duplicate && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          A scan with the same GTIN + {draft.serialNumber ? 'Serial' : 'Batch'} is already in this session.
        </Alert>
      )}
      {draft.errors && draft.errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {draft.errors.join('; ')}
        </Alert>
      )}
      {isUnresolved && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          This code didn&apos;t resolve to an item. Add it to the list and resolve it in the Needs-mapping queue, or map it now.
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>{field('itemNo', 'Item No.')}</Grid>
        <Grid item xs={12} sm={8}>{field('itemDescription', 'Item Description')}</Grid>
        <Grid item xs={12} sm={6}>{field('itemGroup', 'Item Group')}</Grid>
        <Grid item xs={12} sm={6}>{field('pic', 'PIC')}</Grid>
        <Grid item xs={12} sm={6}>{field('gtin', 'GTIN', { mono: true })}</Grid>
        <Grid item xs={12} sm={6}>{field('batchLot', 'Batch/Lot', { mono: true })}</Grid>
        <Grid item xs={6} sm={3}>{field('mfgDate', 'MFG (YYMMDD)', { mono: true })}</Grid>
        <Grid item xs={6} sm={3}>{field('expDate', 'EXP (YYMMDD)', { mono: true })}</Grid>
        <Grid item xs={12} sm={6}>{field('serialNumber', 'Serial number', { mono: true })}</Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Received Qty"
            inputProps={{ min: 1, max: maxQty, step: 'any' }}
            value={qtyValue}
            error={overMax}
            helperText={maxQty != null ? `Open: ${maxQty}` : undefined}
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange({ qty: Number.isFinite(n) && n > 0 ? n : 1 });
            }}
          />
        </Grid>
        {bins.length > 0 && (
          <Grid item xs={12} sm={5}>
            <Autocomplete
              size="small"
              options={bins}
              value={selectedBin}
              getOptionLabel={(b) => b.binCode}
              isOptionEqualToValue={(a, b) => a.absEntry === b.absEntry}
              onChange={(_e, b) =>
                onChange({ binCode: b?.binCode ?? undefined, binAbsEntry: b?.absEntry ?? undefined })
              }
              renderInput={(params) => (
                <TextField {...params} label="Bin location" InputProps={{ ...params.InputProps, sx: { fontFamily: 'monospace' } }} />
              )}
            />
          </Grid>
        )}
      </Grid>

      {overMax && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          Received qty ({qtyValue}) exceeds the open PO quantity ({maxQty}). You cannot receive more than the PO quantity.
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="contained" onClick={onAdd} disabled={overMax}>
          Add to list
        </Button>
        {isUnresolved && (
          <Button variant="outlined" color="warning" onClick={onNeedsMapping}>
            Map / create now
          </Button>
        )}
        <Button onClick={onCancel}>Discard</Button>
      </Stack>
    </Paper>
  );
}
